import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppBar from "../../../common/AppBar.js";
import Snackbar from "../../../common/Snackbar.js";
import RenderHtml from "../../../common/AppSpecificConpnents/PartsTable/RenderHtml.js";
import {
  HeadingWithTreePath,
  generateCompleteTreePath,
} from "../../SubassemblyDetails/utils/treePathUtils.js";

import { getAllSectionsForPart } from "../../../redux/sectionSlice.js";
import {
  getAllAttachmentsForStep,
  getAllAttachmentsForSubstep,
  getAllCanvasesForStep,
  getAllCanvasesForSubstep,
  getAllFormsForStep,
  getAllFormsForSubstep,
  getAllRichTextsForStep,
  getAllRichTextsForSubstep,
  getAllStepsForSection,
  getAllSubstepsForStep,
  getAllVideosForStep,
  getAllVideosForSubstep,
} from "../../../redux/stepSlice.js";

import "../../SubassemblyDetails/SubassemblyDetails.css";
import { getPages, renderPreview, renderVideo } from "../plugins/utils.js";
import {
  applyTransparency,
  calculateGroupHeight,
  calculateTextBlockWidth,
  estimateKonvaTextHeight,
  formatListContent,
  getTextStyle,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  measureHtmlHeight,
  stripHtmlPreserveFormatting,
} from "../plugins/utils.js";
import RectangleElement from "../../Canvas/components/rectangle/RectangleElement.jsx";
import ArrowElement from "../../Canvas/components/arrow/ArrowElement.jsx";
import CircleElement from "../../Canvas/components/circle/CircleElement.jsx";
import LineElement from "../../Canvas/components/line/LineElement.jsx";
import CanvasImage from "../../Canvas/components/canvas-core/CanvasImage.jsx";
import TextElement from "../../Canvas/components/text/TextElement.jsx";
import DoubleSidedArrowElement from "../../Canvas/components/arrow/DoubleSidedArrowElement.jsx";
import CanvasDisplay from "../../Canvas/components/canvas-core/CanvasDisplay.jsx";
const ProcedureSectionMove = () => {
  const [targetSectionData, setTargetSectionData] = useState({});
  const { sections } = useSelector((state) => state.section);
  const [createCopy, setCreateCopy] = useState(false);

  // Global counters to ensure sequential numbering across all sections
  let richTextCounter = 1;
  let canvasCounter = 1;
  let videoCounter = 1;
  let formCounter = 1;
  let attachmentCounter = 1;
  let richTextsCounter = {};

  const location = useLocation();
  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  const {
    type,
    partNumber,
    description,
    revision,
    status,
    title,
    partId,
    fromTreeId,
    fromSectionId,
    revisionId,
    fromSiblingOrder,
  } = location.state;

  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const [isProcedureSelected, setIsProcedureSelected] = useState(false);

  const {
    stepsBySection,
    substepsByStep,
    richTexts,
    attachments,
    forms,
    canvases,
    videos,
  } = useSelector((state) => state.step);

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const sortedSections = [...sections];

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCreateCopyChange = () => {
    setCreateCopy(!createCopy);
  };

  const handleMoveUp = () => {
    const sourceSectionIndex = parseInt(title.split(" ")[1]) - 1;
    const targetSectionIndex = selectedSectionIndex;

    if (isProcedureSelected) {
      showSnackbar(
        "Please use Move Into button for moving into procedure",
        "error"
      );
      return;
    }

    if (targetSectionIndex === null) {
      showSnackbar("Please select a target section to move to", "error");
      return;
    }

    if (sourceSectionIndex === targetSectionIndex) {
      showSnackbar("Cannot move section to its current position", "error");
      return;
    }

    // Debug the source and target information
    // Create state to pass to preview
    const stateToPass = {
      ...location.state,
      moveType: "up",
      sourceSectionIndex,
      targetSectionIndex,
      revision,
      type,
      partNumber,
      revisionId,
      targetSectionData,
      partId,
      fromSectionId,
      fromTreeId,
      fromSiblingOrder,
      createCopy,
    };

    // Log the state being passed
    // Navigate to preview screen
    navigate("/procedure-section-move-preview", {
      state: stateToPass,
    });
  };

  const handleMoveBelow = () => {
    const sourceSectionIndex = parseInt(title.split(" ")[1]) - 1;
    const targetSectionIndex = selectedSectionIndex;

    if (isProcedureSelected) {
      showSnackbar(
        "Please use Move Into button for moving into procedure",
        "error"
      );
      return;
    }

    if (targetSectionIndex === null) {
      showSnackbar("Please select a target section to move to", "error");
      return;
    }

    if (sourceSectionIndex === targetSectionIndex) {
      showSnackbar("Cannot move section to its current position", "error");
      return;
    }

    // Debug the source and target information
    // Create state to pass to preview
    const stateToPass = {
      ...location.state,
      moveType: "below",
      sourceSectionIndex,
      targetSectionIndex,
      revisionId,
      targetSectionData,
      partId,
      fromSectionId,
      fromTreeId,
      fromSiblingOrder,
      createCopy,
    };

    // Log the state being passed
    // Navigate to preview screen
    navigate("/procedure-section-move-preview", {
      state: stateToPass,
    });
  };

  const handleMoveInto = () => {
    const sourceSectionIndex = parseInt(title.split(" ")[1]) - 1;

    if (!isProcedureSelected) {
      showSnackbar(
        "Please select the procedure checkbox to move section into it",
        "error"
      );
      return;
    }

    const lastSectionIndex = sortedSections.length - 1;

    // if (sourceSectionIndex === lastSectionIndex) {
    //   showSnackbar("This section is already at the last position", "error");
    //   return;
    // }

    // ✅ Get highest sibling_order (as a string, but sort numerically)
    const highestSiblingOrder = sortedSections.reduce((max, section) => {
      const order = parseInt(section.sibling_order || 0); // safely convert to number
      return order > max ? order : max;
    }, -Infinity);

    // Create a proper targetSectionData for moving into procedure
    const targetSectionDataForInto = {
      siblingOrder: highestSiblingOrder + 1,
      sectionTreeId: revisionId,
      sectionId: null, // No target section when moving into procedure
      sectionName: `${type} ${partNumber}`,
      sectionIndex: -1, // Special index to indicate procedure root
    };

    // Create state to pass to preview
    const stateToPass = {
      ...location.state,
      moveType: "into",
      sourceSectionIndex,
      targetSectionIndex: lastSectionIndex,
      moveIntoProcedure: true,
      revisionId,
      targetSectionData: targetSectionDataForInto,
      partId,
      fromSectionId,
      fromTreeId,
      fromSiblingOrder,
      createCopy,
    };

    // Log the state being passed
    // Navigate to preview screen
    navigate("/procedure-section-move-preview", {
      state: stateToPass,
    });
  };

  const handleProcedureCheckboxChange = () => {
    // When procedure is selected, clear any section selection
    if (!isProcedureSelected) {
      setSelectedSectionIndex(null);
    }
    setIsProcedureSelected(!isProcedureSelected);
  };

  // Create buttons array conditionally based on selection state
  const getButtons = () => {
    const buttons = [];

    // If procedure is selected, only show Move Into/Copy Into button
    if (isProcedureSelected) {
      buttons.push({
        label: createCopy ? "Paste Into" : "Move Into",
        onClick: handleMoveInto,
      });
    }
    // If section is selected, only show Move Up/Copy Up and Move Below/Copy Below buttons
    else if (selectedSectionIndex !== null) {
      buttons.push({
        label: createCopy ? "Paste Above" : "Move Up",
        onClick: handleMoveUp,
      });
      buttons.push({
        label: createCopy ? "Paste Below" : "Move Below",
        onClick: handleMoveBelow,
      });
    }
    // If nothing is selected, show no buttons
    else {
      // No buttons shown when no checkbox is selected
    }

    return buttons;
  };

  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      // setShowLoader(true);

      await dispatch(getAllSectionsForPart({ partId, revisionId }));
    })();
  }, [dispatch, partId, revisionId]);

  useEffect(() => {
    if (sections.length > 0) {
      sections.forEach(async (section, index) => {
        await dispatch(
          getAllStepsForSection({
            partId,
            revisionId,
            sectionId: section.section_tree_node_id,
          })
        );
      });
    }
  }, [sections, dispatch, partId, revisionId]);

  useEffect(() => {
    if (Object.keys(stepsBySection).length > 0) {
      Object.entries(stepsBySection).forEach(([sectionId, steps], index) => {
        steps.forEach(async (step) => {
          const { step_tree_node_id: stepId } = step;

          // // Fetch for step-level content
          await dispatch(
            getAllRichTextsForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllFormsForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllVideosForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllAttachmentsForStep({ partId, revisionId, sectionId, stepId })
          );
          await dispatch(
            getAllCanvasesForStep({ partId, revisionId, sectionId, stepId })
          );
          // Fetch substeps (async)
          await dispatch(
            getAllSubstepsForStep({ partId, revisionId, sectionId, stepId })
          ).then((action) => {
            const substeps = action.payload?.substeps || [];
            substeps.forEach((substep) => {
              const substepId = substep.sub_step_tree_node_id;

              dispatch(
                getAllRichTextsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllFormsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllVideosForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllAttachmentsForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
              dispatch(
                getAllCanvasesForSubstep({
                  partId,
                  revisionId,
                  sectionId,
                  stepId,
                  substepId,
                })
              );
            });
          });
          if (index === Object.entries(stepsBySection).length - 1) {
            // setShowLoader(false);
          }
        });
      });
    } else {
      // setShowLoader(false);
    }
  }, [sections, dispatch, partId, revisionId, stepsBySection]);

  return (
    <div>
      <AppBar
        showTablesDropdown={false}
        showBackButton={false}
        showCancelButton={true}
        showCreateCopyCheckbox={true}
        createCopy={createCopy}
        onCreateCopyChange={handleCreateCopyChange}
        title={`${createCopy ? "Copying" : "Moving"} ${title} ${
          selectedSectionIndex !== null &&
          selectedSectionIndex + 1 != title.split(" ")[1].trim()
            ? `to Section ${selectedSectionIndex + 1}`
            : isProcedureSelected
            ? `to ${type} ${partNumber}`
            : ""
        }`}
        buttons={getButtons()}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          padding: "8px 0",
          flexWrap: "wrap", // for responsiveness
        }}
      >
        {/* Checkbox + Title + Description */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              margin: 0,
            }}
          >
            <input
              type="checkbox"
              onChange={handleProcedureCheckboxChange}
              checked={isProcedureSelected}
              style={
                isProcedureSelected
                  ? {
                      accentColor: "#0066cc",
                      backgroundColor: "#0066cc",
                      color: "#0066cc",
                    }
                  : {}
              }
            />
            <span
              style={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#0066cc", // Blue color for procedure
              }}
            >
              {type} {partNumber}
            </span>
            {isProcedureSelected && (
              <span
                style={{
                  marginLeft: "12px",
                  fontSize: "14px",
                  color: "#666",
                  fontStyle: "italic",
                }}
              >
                {/* (Move to end) */}
              </span>
            )}
          </label>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginLeft: "auto",
          }}
        >
          <div>
            <strong>Status: </strong>
            <span style={{ color: "orange" }}>{status}</span>
          </div>

          <div>
            <strong>Revision: </strong>
            <span>{revision}</span>
          </div>
        </div>
      </div>

      {sortedSections
        .sort((a, b) => {
          const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
          const orderB = b.sibling_order || b.procedure_tree?.sibling_order;

          const orderCompare = compareSiblingOrders(orderA, orderB);
          if (orderCompare !== 0) return orderCompare;

          // Fallback: compare created_at
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return dateB - dateA; // latest comes first
        })
        .map((section, index) => {
          const selectedSection = index + 1 == title.split(" ")[1].trim();
          const isDisabled = selectedSection;

          return (
            <div
              key={section.section_tree_node_id}
              style={{
                margin: "20px 0",
                clear: "both",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  clear: "both",
                }}
              >
                <input
                  type="checkbox"
                  style={
                    selectedSection
                      ? {
                          accentColor: "#444444", // Dark grey for section being moved
                          backgroundColor: "#444444",
                        }
                      : {
                          accentColor: "#0066cc", // Blue color for other sections
                          backgroundColor: "#0066cc",
                        }
                  }
                  checked={selectedSection || selectedSectionIndex === index}
                  onChange={() => {
                    if (!isDisabled) {
                      // If clicking the already selected checkbox, unselect it
                      if (selectedSectionIndex === index) {
                        setSelectedSectionIndex(null);
                        setTargetSectionData({});
                      } else {
                        setSelectedSectionIndex(index);
                        // When a section is selected, deselect procedure
                        setIsProcedureSelected(false);

                        // Log section details for debugging
                        setTargetSectionData({
                          sectionId: section.section_tree_node_id,
                          siblingOrder: section.sibling_order || 0,
                          sectionTreeId:
                            section.procedure_tree_ref || revisionId,
                          sectionName: section.section_name,
                          sectionIndex: index,
                        });
                      }
                    }
                  }}
                  disabled={false}
                />

                {/* Simple heading for sections without tree path */}
                <h2
                  style={{
                    fontSize: "20px",
                    margin: 0,
                    color: selectedSection ? "#444444" : "#0066cc", // Dark grey for section being moved, blue for others
                    clear: "both",
                  }}
                >
                  Section {index + 1}
                </h2>
                {selectedSection && (
                  <span
                    style={{
                      marginLeft: "12px",
                      fontSize: "14px",
                      color: "#666",
                      fontStyle: "italic",
                    }}
                  >
                    {/* (Current Section Being Moved) */}
                  </span>
                )}
              </label>
              <hr />
              <hr />
              <div>{section.section_name}</div>
              <div>{section.short_description}</div>

              {/* Steps for this section */}
              {(stepsBySection[section.section_tree_node_id] || []).map(
                (step, stepIndex) => {
                  const stepId = step.step_tree_node_id;

                  // Calculate the global step number by counting steps in all previous sections
                  const stepsBeforeThisSection = sortedSections
                    .slice(0, index)
                    .reduce(
                      (acc, s) =>
                        acc +
                        (stepsBySection[s.section_tree_node_id]?.length || 0),
                      0
                    );
                  const globalStepNumber =
                    stepsBeforeThisSection + stepIndex + 1;

                  // Store the section index to use in the inner loops
                  const currentSectionIndex = index;

                  const combinedItems = [
                    ...(richTexts[stepId] || []).map((item) => ({
                      ...item,
                      type: "richText",
                    })),
                    ...(forms[stepId] || []).map((item) => ({
                      ...item,
                      type: "form",
                    })),
                    ...(attachments[stepId] || []).map((item) => ({
                      ...item,
                      type: "attachment",
                    })),
                    ...(canvases[stepId] || []).map((item) => ({
                      ...item,
                      type: "canvas",
                    })),
                    ...(videos[stepId] || []).map((item) => ({
                      ...item,
                      type: "video",
                    })),
                  ]; // Filter out substeps or unwanted items (same as your current filter)
                  const filteredItems = combinedItems.filter(
                    (item) =>
                      !item.substep_id_ref && !item.sub_step_tree_node_id
                  );

                  filteredItems.sort((a, b) => {
                    const orderA =
                      a.sibling_order || a.procedure_tree?.sibling_order;
                    const orderB =
                      b.sibling_order || b.procedure_tree?.sibling_order;

                    const orderCompare = compareSiblingOrders(orderA, orderB);
                    if (orderCompare !== 0) return orderCompare;

                    // Fallback: compare created_at
                    const dateA = new Date(a.created_at);
                    const dateB = new Date(b.created_at);
                    return dateB - dateA; // latest comes first
                  });

                  return (
                    <div
                      key={stepId}
                      style={{ marginTop: "20px", clear: "both" }}
                    >
                      <label
                        style={{
                          display: "flex",
                          gap: "8px",
                          cursor: "pointer",
                          clear: "both",
                        }}
                      >
                        {selectedSection && (
                          <input
                            type="checkbox"
                            checked={true}
                            disabled={true}
                            style={{
                              accentColor: "#444444",
                              backgroundColor: "#444444",
                            }}
                          />
                        )}
                        <HeadingWithTreePath
                          title={`Step ${globalStepNumber}`}
                          treePathParams={{
                            sectionIndex: index,
                            stepIndex: globalStepNumber - 1,
                            substepIndex: undefined,
                            nodeType: undefined,
                            nodeIndex: undefined,
                          }}
                        />
                      </label>
                      <hr />
                      <p style={{ whiteSpace: 'pre-wrap' }}>{step.short_description || ""}</p>
                      <p>
                        <p>
                          {structuredClone(step.step_details)
                            .sort(
                              (a, b) =>
                                new Date(a.created_at) - new Date(b.created_at)
                            )
                            .map((myStep) => {
                              const partNumber =
                                myStep.part_info?.part_number || "";
                              const description =
                                myStep.part_info?.description || "";
                              const qtyText =
                                myStep.qty && myStep.qty != 0
                                  ? `Qty ${myStep.qty}`
                                  : "";

                              const partText = partNumber
                                ? ` (${partNumber})`
                                : "";

                              return (
                                <>
                                  {myStep.action}
                                  {partText}{" "}
                                  <RenderHtml content={description} /> {qtyText}
                                  &nbsp;
                                </>
                              );
                            })}
                        </p>
                      </p>

                      <h4>{step.bom_table_title_info?.category_name || ""}</h4>

                      <table className="table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>
                              {
                                step.part_number_column_title_ref_info
                                  ?.category_name
                              }{" "}
                            </th>
                            <th>Description</th>
                            <th>Qty Per</th>
                            <th>Unit</th>
                          </tr>
                        </thead>

                        <tbody>
                          {step.parts_required.map((row) => (
                            <tr>
                              <td>{row.part_info.type_info.name}</td>
                              <td>{row.part_info.part_number}</td>
                              <td>
                                <RenderHtml
                                  content={row.part_info.description}
                                />{" "}
                              </td>
                              <td>{row.qty === 0 ? "" : row.qty}</td>
                              <td>{row.part_info?.unit_info?.unit_name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Step Content */}

                      {filteredItems.map((item, itemIndex) => {
                        if (item.substep_id_ref || item.sub_step_tree_node_id)
                          return null;
                        switch (item.type) {
                          case "richText": {
                            const globalRichTextNumber = richTextCounter++; // Increment the local counter
                            // render richText block here
                            const label = (
                              <h2 style={{ fontSize: "20px", margin: 0 }}>
                                Rich Text {globalRichTextNumber}
                              </h2>
                            );

                            return (
                              <div
                                key={item.rich_text_tree_node_id}
                                style={{ marginTop: "20px", clear: "both" }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    clear: "both",
                                    marginTop: "20px",
                                    marginBottom: "20px",
                                  }}
                                >
                                  <label
                                    style={{
                                      display: "flex",
                                      gap: "6px",
                                      cursor: "pointer",
                                      clear: "both",
                                      marginTop: "20px",
                                      marginBottom: "20px",
                                    }}
                                  >
                                    {selectedSection && (
                                      <input
                                        type="checkbox"
                                        checked={true}
                                        disabled={true}
                                        style={{
                                          accentColor: "#444444",
                                          backgroundColor: "#444444",
                                        }}
                                      />
                                    )}
                                    {label}
                                  </label>

                                  <div
                                    style={{
                                      color: "#888",
                                      fontSize: "14px",
                                      fontWeight: "normal",
                                      clear: "both",
                                    }}
                                  >
                                    Section {currentSectionIndex + 1} &gt; Step{" "}
                                    {globalStepNumber}
                                  </div>
                                </div>

                                <div
                                  style={{
                                    marginLeft: "5px",
                                    marginTop: "-10px",
                                    clear: "both",
                                  }}
                                >
                                  {item.short_description}
                                </div>
                                <div
                                  style={{
                                    marginLeft: "4px",
                                    marginTop: "5px",
                                    clear: "both",
                                  }}
                                >
                                  {item.category_data?.category_name}
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    marginLeft: "4px",
                                    marginTop: "5px",
                                    clear: "both",
                                  }}
                                >
                                  <div
                                    style={{ marginTop: "-6px", clear: "both" }}
                                    dangerouslySetInnerHTML={{
                                      __html: item.rich_text_content,
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          }
                          case "video": {
                            const globalVideoNumber = videoCounter++; // Increment the local counter
                            const label = (
                              <h2 style={{ fontSize: "20px", margin: 0 }}>
                                Video {globalVideoNumber}
                              </h2>
                            );

                            return (
                              <div
                                key={label}
                                style={{ marginTop: "20px", clear: "both" }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    clear: "both",
                                  }}
                                >
                                  <label
                                    style={{
                                      display: "flex",
                                      gap: "6px",
                                      cursor: "pointer",
                                      clear: "both",
                                    }}
                                  >
                                    {selectedSection && (
                                      <input
                                        type="checkbox"
                                        checked={true}
                                        disabled={true}
                                        style={{
                                          accentColor: "#444444",
                                          backgroundColor: "#444444",
                                        }}
                                      />
                                    )}
                                    {label}
                                  </label>

                                  <div
                                    style={{
                                      color: "#888",
                                      fontSize: "14px",
                                      fontWeight: "normal",
                                      clear: "both",
                                    }}
                                  >
                                    Section {currentSectionIndex + 1} &gt; Step{" "}
                                    {globalStepNumber}
                                  </div>
                                </div>

                                {renderVideo(item)}

                                <div>{item.video_url}</div>

                                <div>{item.short_description}</div>
                              </div>
                            );
                          }

                          case "form": {
                            const globalFormNumber = formCounter++; // Increment the local counter
                            const label = (
                              <h2 style={{ fontSize: "20px", margin: 0 }}>
                                Form {globalFormNumber}
                              </h2>
                            );
                            // render form block here
                            return (
                              <div
                                key={label}
                                style={{ marginTop: "20px", clear: "both" }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginTop: "20px",
                                    clear: "both",
                                  }}
                                >
                                  <label
                                    style={{
                                      display: "flex",
                                      gap: "6px",
                                      cursor: "pointer",
                                      clear: "both",
                                    }}
                                  >
                                    {selectedSection && (
                                      <input
                                        type="checkbox"
                                        checked={true}
                                        disabled={true}
                                        style={{
                                          accentColor: "#444444",
                                          backgroundColor: "#444444",
                                        }}
                                      />
                                    )}
                                    {label}
                                  </label>

                                  <div
                                    style={{
                                      color: "#888",
                                      fontSize: "14px",
                                      fontWeight: "normal",
                                      clear: "both",
                                    }}
                                  >
                                    Section {currentSectionIndex + 1} &gt; Step{" "}
                                    {globalStepNumber}
                                  </div>
                                </div>
                                <div>{item.short_description}</div>
                              </div>
                            );
                          }
                          case "canvas": {
                            const pages = getPages(item);

                            const globalCanvasNumber = canvasCounter++; // Increment the local counter
                            const label = (
                              <h2 style={{ fontSize: "20px", margin: 0 }}>
                                Canvas {globalCanvasNumber}
                              </h2>
                            );
                            const shapes = item.shapes || [];

                            return (
                              <div
                                key={label}
                                style={{ marginTop: "40px", clear: "both" }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    clear: "both",
                                  }}
                                >
                                  <label
                                    style={{
                                      display: "flex",
                                      gap: "6px",
                                      cursor: "pointer",
                                      clear: "both",
                                    }}
                                  >
                                    {selectedSection && (
                                      <input
                                        type="checkbox"
                                        checked={true}
                                        disabled={true}
                                        style={{
                                          accentColor: "#444444",
                                          backgroundColor: "#444444",
                                        }}
                                      />
                                    )}
                                    {label}
                                  </label>

                                  <div
                                    style={{
                                      color: "#888",
                                      fontSize: "14px",
                                      fontWeight: "normal",
                                      clear: "both",
                                    }}
                                  >
                                    Section {currentSectionIndex + 1} &gt; Step{" "}
                                    {globalStepNumber}
                                  </div>
                                </div>

                                <div>{item.short_description}</div>

                                {/* Display all pages vertically */}
                                <CanvasDisplay pages={pages} />
                              </div>
                            );
                          }
                          case "attachment": {
                            const attachmentList = item.attachments || [];
                            if (attachmentList.length === 0) return null;

                            const globalAttachmentNumber = attachmentCounter++; // Increment the local counter
                            const label = (
                              <h2 style={{ fontSize: "20px", margin: 0 }}>
                                Attachment {globalAttachmentNumber}
                              </h2>
                            );

                            return (
                              <div
                                key={item}
                                style={{ marginTop: "20px", clear: "both" }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginTop: "20px",
                                    clear: "both",
                                  }}
                                >
                                  <label
                                    style={{
                                      display: "flex",
                                      gap: "6px",
                                      cursor: "pointer",
                                      clear: "both",
                                    }}
                                  >
                                    {selectedSection && (
                                      <input
                                        type="checkbox"
                                        checked={true}
                                        disabled={true}
                                        style={{
                                          accentColor: "#444444",
                                          backgroundColor: "#444444",
                                        }}
                                      />
                                    )}
                                    {label}
                                  </label>

                                  <div
                                    style={{
                                      color: "#888",
                                      fontSize: "14px",
                                      fontWeight: "normal",
                                      clear: "both",
                                    }}
                                  >
                                    Section {currentSectionIndex + 1} &gt; Step{" "}
                                    {globalStepNumber}
                                  </div>
                                </div>
                                <div>{item.short_description}</div>
                                {attachmentList.map((attachment, i) => (
                                  <div key={i} style={{ marginLeft: "20px" }}>
                                    {renderPreview(attachment)}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          default:
                            return null;
                        }
                      })}

                      {/* Substeps */}
                      {(substepsByStep[stepId] || []).map(
                        (substep, substepIndex) => {
                          const substepId = substep.sub_step_tree_node_id;
                          const combinedItems = [
                            ...(richTexts[substepId] || []).map((item) => ({
                              ...item,
                              type: "richText",
                            })),
                            ...(forms[substepId] || []).map((item) => ({
                              ...item,
                              type: "form",
                            })),
                            ...(attachments[substepId] || []).map((item) => ({
                              ...item,
                              type: "attachment",
                            })),
                            ...(canvases[substepId] || []).map((item) => ({
                              ...item,
                              type: "canvas",
                            })),
                            ...(videos[substepId] || []).map((item) => ({
                              ...item,
                              type: "video",
                            })),
                          ]; // Filter out substeps or unwanted items (same as your current filter)
                          const filteredItems = combinedItems;

                          filteredItems.sort((a, b) => {
                            const orderA =
                              a.sibling_order ||
                              a.procedure_tree?.sibling_order;
                            const orderB =
                              b.sibling_order ||
                              b.procedure_tree?.sibling_order;

                            const orderCompare = compareSiblingOrders(
                              orderA,
                              orderB
                            );
                            if (orderCompare !== 0) return orderCompare;

                            // Fallback: compare created_at
                            const dateA = new Date(a.created_at);
                            const dateB = new Date(b.created_at);
                            return dateB - dateA; // latest comes first
                          });

                          // Use the current step number as the base for the substep
                          // Use the calculated global step number for the substep label
                          // Final substep label like Substep 5A
                          const substepLetter = String.fromCharCode(
                            65 + substepIndex
                          ); // A, B, C, ...

                          const globalSubstepLabel = `Substep ${globalStepNumber}${substepLetter}`;

                          return (
                            <div
                              key={substepId}
                              style={{ marginTop: "20px", clear: "both" }}
                            >
                              <div>
                                <label
                                  style={{
                                    display: "flex",
                                    gap: "8px",
                                    cursor: "pointer",
                                    clear: "both",
                                  }}
                                >
                                  {selectedSection && (
                                    <input
                                      type="checkbox"
                                      checked={true}
                                      disabled={true}
                                      style={{
                                        accentColor: "#444444",
                                        backgroundColor: "#444444",
                                      }}
                                    />
                                  )}
                                  <HeadingWithTreePath
                                    title={globalSubstepLabel}
                                    treePathParams={{
                                      sectionIndex: index,
                                      stepIndex: globalStepNumber - 1,
                                      substepIndex: substepIndex,
                                      nodeType: undefined,
                                      nodeIndex: undefined,
                                    }}
                                  />
                                </label>

                                <div style={{ whiteSpace: 'pre-wrap' }}>{substep.short_description}</div>
                              </div>

                              {/* Substep Rich Texts */}
                              {filteredItems.map((item, itemIndex) => {
                                switch (item.type) {
                                  case "richText": {
                                    const globalRichTextNumber =
                                      richTextCounter++; // Increment the local counter
                                    // render richText block here
                                    const label = (
                                      <h2
                                        style={{ fontSize: "20px", margin: 0 }}
                                      >
                                        Rich Text {globalRichTextNumber}
                                      </h2>
                                    );

                                    return (
                                      <div
                                        key={item.rich_text_tree_node_id}
                                        style={{
                                          marginTop: "20px",
                                          clear: "both",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            clear: "both",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                              clear: "both",
                                            }}
                                          >
                                            {selectedSection && (
                                              <input
                                                type="checkbox"
                                                checked={true}
                                                disabled={true}
                                                style={{
                                                  accentColor: "#444444",
                                                  backgroundColor: "#444444",
                                                }}
                                              />
                                            )}
                                            {label}
                                          </label>

                                          <div
                                            style={{
                                              color: "#888",
                                              fontSize: "14px",
                                              fontWeight: "normal",
                                              clear: "both",
                                            }}
                                          >
                                            Section {currentSectionIndex + 1}{" "}
                                            &gt; Step {globalStepNumber} &gt;
                                            Substep {globalStepNumber}
                                            {String.fromCharCode(
                                              65 + substepIndex
                                            )}
                                          </div>
                                        </div>

                                        <div
                                          style={{
                                            marginLeft: "5px",
                                            marginTop: "-10px",
                                            clear: "both",
                                          }}
                                        >
                                          {item.short_description}
                                        </div>
                                        <div
                                          style={{
                                            marginLeft: "4px",
                                            marginTop: "5px",
                                            clear: "both",
                                          }}
                                        >
                                          {item.category_data?.category_name}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            marginLeft: "4px",
                                            marginTop: "5px",
                                            clear: "both",
                                          }}
                                        >
                                          <div
                                            style={{
                                              marginTop: "-6px",
                                              clear: "both",
                                            }}
                                            dangerouslySetInnerHTML={{
                                              __html: item.rich_text_content,
                                            }}
                                          />
                                        </div>
                                      </div>
                                    );
                                  }
                                  case "video": {
                                    const globalVideoNumber = videoCounter++; // Increment the local counter
                                    const label = (
                                      <h2
                                        style={{ fontSize: "20px", margin: 0 }}
                                      >
                                        Video {globalVideoNumber}
                                      </h2>
                                    );

                                    return (
                                      <div
                                        key={label}
                                        style={{
                                          marginTop: "20px",
                                          clear: "both",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            clear: "both",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                              clear: "both",
                                            }}
                                          >
                                            {selectedSection && (
                                              <input
                                                type="checkbox"
                                                checked={true}
                                                disabled={true}
                                                style={{
                                                  accentColor: "#444444",
                                                  backgroundColor: "#444444",
                                                }}
                                              />
                                            )}
                                            {label}
                                          </label>

                                          <div
                                            style={{
                                              color: "#888",
                                              fontSize: "14px",
                                              fontWeight: "normal",
                                              clear: "both",
                                            }}
                                          >
                                            Section {currentSectionIndex + 1}{" "}
                                            &gt; Step {globalStepNumber} &gt;
                                            Substep {globalStepNumber}
                                            {String.fromCharCode(
                                              65 + substepIndex
                                            )}
                                          </div>
                                        </div>

                                        {renderVideo(item)}

                                        <div>{item.video_url}</div>

                                        <div>{item.short_description}</div>
                                      </div>
                                    );
                                  }

                                  case "form": {
                                    const globalFormNumber = formCounter++; // Increment the local counter
                                    const label = (
                                      <h2
                                        style={{ fontSize: "20px", margin: 0 }}
                                      >
                                        Form {globalFormNumber}
                                      </h2>
                                    );
                                    // render form block here
                                    return (
                                      <div
                                        key={label}
                                        style={{
                                          marginTop: "20px",
                                          clear: "both",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginTop: "20px",
                                            clear: "both",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                              clear: "both",
                                            }}
                                          >
                                            {selectedSection && (
                                              <input
                                                type="checkbox"
                                                checked={true}
                                                disabled={true}
                                                style={{
                                                  accentColor: "#444444",
                                                  backgroundColor: "#444444",
                                                }}
                                              />
                                            )}
                                            {label}
                                          </label>

                                          <div
                                            style={{
                                              color: "#888",
                                              fontSize: "14px",
                                              fontWeight: "normal",
                                              clear: "both",
                                            }}
                                          >
                                            Section {currentSectionIndex + 1}{" "}
                                            &gt; Step {globalStepNumber} &gt;
                                            Substep {globalStepNumber}
                                            {String.fromCharCode(
                                              65 + substepIndex
                                            )}
                                          </div>
                                        </div>
                                        <div>{item.short_description}</div>
                                      </div>
                                    );
                                  }
                                  case "canvas": {
                                    const pages = getPages(item);

                                    const globalCanvasNumber = canvasCounter++; // Increment the local counter
                                    const label = (
                                      <h2
                                        style={{ fontSize: "20px", margin: 0 }}
                                      >
                                        Canvas {globalCanvasNumber}
                                      </h2>
                                    );
                                    const shapes = item.shapes || [];

                                    return (
                                      <div
                                        key={label}
                                        style={{
                                          marginTop: "40px",
                                          clear: "both",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            clear: "both",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                              clear: "both",
                                            }}
                                          >
                                            {selectedSection && (
                                              <input
                                                type="checkbox"
                                                checked={true}
                                                disabled={true}
                                                style={{
                                                  accentColor: "#444444",
                                                  backgroundColor: "#444444",
                                                }}
                                              />
                                            )}
                                            {label}
                                          </label>

                                          <div
                                            style={{
                                              color: "#888",
                                              fontSize: "14px",
                                              fontWeight: "normal",
                                              clear: "both",
                                            }}
                                          >
                                            Section {currentSectionIndex + 1}{" "}
                                            &gt; Step {globalStepNumber} &gt;
                                            Substep {globalStepNumber}
                                            {String.fromCharCode(
                                              65 + substepIndex
                                            )}
                                          </div>
                                        </div>

                                        <div>{item.short_description}</div>

                                        {/* Display all pages vertically */}
                                        <CanvasDisplay pages={pages} />
                                      </div>
                                    );
                                  }
                                  case "attachment": {
                                    const attachmentList =
                                      item.attachments || [];
                                    if (attachmentList.length === 0)
                                      return null;

                                    const globalAttachmentNumber =
                                      attachmentCounter++; // Increment the local counter
                                    const label = (
                                      <h2
                                        style={{ fontSize: "20px", margin: 0 }}
                                      >
                                        Attachment {globalAttachmentNumber}
                                      </h2>
                                    );

                                    return (
                                      <div key={item}>
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginTop: "20px",
                                            clear: "both",
                                          }}
                                        >
                                          <label
                                            style={{
                                              display: "flex",
                                              gap: "6px",
                                              cursor: "pointer",
                                              clear: "both",
                                            }}
                                          >
                                            {selectedSection && (
                                              <input
                                                type="checkbox"
                                                checked={true}
                                                disabled={true}
                                                style={{
                                                  accentColor: "#444444",
                                                  backgroundColor: "#444444",
                                                }}
                                              />
                                            )}
                                            {label}
                                          </label>

                                          <div
                                            style={{
                                              color: "#888",
                                              fontSize: "14px",
                                              fontWeight: "normal",
                                              clear: "both",
                                            }}
                                          >
                                            Section {currentSectionIndex + 1}{" "}
                                            &gt; Step {globalStepNumber} &gt;
                                            Substep {globalStepNumber}
                                            {String.fromCharCode(
                                              65 + substepIndex
                                            )}
                                          </div>
                                        </div>
                                        <div>{item.short_description}</div>
                                        {attachmentList.map((attachment, i) => (
                                          <div
                                            key={i}
                                            style={{
                                              marginLeft: "20px",
                                              clear: "both",
                                            }}
                                          >
                                            {renderPreview(attachment)}
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  }
                                  default:
                                    return null;
                                }
                              })}
                            </div>
                          );
                        }
                      )}
                    </div>
                  );
                }
              )}
            </div>
          );
        })}

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
      <div style={{ height: "100px" }}></div>
    </div>
  );
};
export default ProcedureSectionMove;
